<script lang="ts" setup>
import { ref, computed } from 'vue'
import { $date } from '@/plugins'
import i18n from '@/scripts/app-configs/i18n-config'
import PatientAttentionLevel from '@/components/patient/PatientAttentionLevel.vue'
import GutenbergHighlight from '@/components/micro/GutenbergHighlight.vue'
import QuickAction from '@/components/inputs/QuickAction.vue'
import { QUICK_ACTIONS_CONFIG } from '@/components/drawers/MemberDrawer/constants/quick-actions-config'
import type { Profile, Eligibility, Context } from '@/components/drawers/MemberDrawer/types/member-drawer.types'

interface HeaderProps {
  profile: Profile
  age?: number | string,
  currentState: string | null
  eligibility: Eligibility
}

const props = defineProps<HeaderProps>()

const emit = defineEmits<{
  (e: 'copy-to-clipboard', payload: { context: Context, content?: string }): void,
  (e: 'open-ui', payload: { context: Context }): void,
  (e: 'send-to-user', payload: { context: Context }): void,
}>()

const attentionLevel = ref(props.profile.member_attention_level)

const currentStateDetails = computed(() => {
  return {
    cssClass: props.currentState
      ? `patient-state-${props.currentState.replace(/_/g, '-')}`
      : 'patient-state-unknown',
    copy: props.currentState ? `patient_state_${props.currentState}` : '-',
  }
})

const quickActions = QUICK_ACTIONS_CONFIG.filter((action) => !action.disabled)

const getChipTitle = () => {
  if (props.profile.remaining_days_in_on_hold === 0) {
    return i18n.t('LATEST_ACTIVITY.TODAY')
  }

  return i18n.t('n_days_left', { daysleft: props.profile.remaining_days_in_on_hold })
}

const handleClickAction = (action: string) => {
  const context = action.replace(/^[a-z]+-/, '') as Context

  switch (action) {
    case 'open-notes':
    case 'open-sword-desk':
      emit('open-ui', { context })
      break
    case 'copy-reassessment-link':
    case 'copy-ob-call-link':
    case 'copy-check-in-link':
      emit('copy-to-clipboard', { context })
      break
    case 'send-reassessment-link':
    case 'send-check-in-link':
      emit('send-to-user', { context })
      break
    default:
      break
  }
}

const copyToClipboard = (content: string) => {
  emit('copy-to-clipboard', { context: 'member-id', content })
}
</script>

<template>
  <header class="flex flex-col items-center justify-center border-b border-context-6 p-4 font-normal">
    <section class="mb-5 flex flex-col items-center justify-center">
      <router-link
        class="rounded-full border border-transparent transition-colors duration-200 ease-in-out hover:border-primary"
        :to="`/patients/${profile.user_id}/board`"
        target="_blank"
        rel="noreferrer noopener"
      >
        <profile-picture
          :gender="profile.gender"
          :show-vip-icon="profile.is_vip_account"
          :show-high-risk-icon="profile.patient_is_high_risk"
          :show-gender-icon="!!profile.gender"
          :picture="profile.picture"
          class="!size-16"
        />
      </router-link>
    </section>

    <patient-attention-level v-model="attentionLevel" :patient-id="profile.user_id" class="mb-2" editable />

    <section class="flex flex-col items-center gap-1">
      <div class="flex items-center">
        <gutenberg-highlight v-if="profile.gutenberg_flag"/>
        <p class="mb-0 flex items-center">
          <router-link
            class="text-inherit hover:text-primary"
            :to="`/patients/${profile.user_id}/board`"
            target="_blank"
            rel="noreferrer noopener"
          >
            {{ `${profile.firstname} ${profile.lastname}` }}
          </router-link>
          <template v-if="!!age">
            <span class="dot-separator mx-2"></span>
            <span>{{ $t("copy_1288") }} {{ age }}</span>
          </template>
        </p>
      </div>

      <div class="flex items-center gap-2">
        <div class="flex items-center gap-1">
          <span class="size-1.5 rounded-full" :class="`${currentStateDetails.cssClass}-bg`"></span>
          <span class="t3">{{ $t(currentStateDetails.copy) }}</span>
          <template v-if="profile.remaining_days_in_on_hold !== null">
            <chip-wuk
              :title="getChipTitle"
              color="primary"
              rounded
              bold
            >
              {{ profile.remaining_days_in_on_hold }}d
            </chip-wuk>
          </template>
        </div>
        <span class="dot-separator"></span>
        <div class="flex items-center gap-1">
          <span>{{ $t("copy_4331") }}</span>
          <hover-to-copy @on-copy="copyToClipboard">
            {{ profile.user_id }}
          </hover-to-copy>
        </div>
        <span class="dot-separator"></span>
        <div>
          <span>{{ profile.institution_name }}</span>
        </div>
      </div>

      <!-- Aso name -->
      <p v-if="eligibility.aso_name" class="flex gap-1">{{ $t("member_info.aso_group") }} {{ eligibility.aso_name }}</p>

      <sword-tag
        v-if="!!profile.has_pending_exclusion_requests"
        color="neutral"
        size="sm"
      >
        {{ $t('SIDEBAR.PENDING_EXCLUSION') }}
      </sword-tag>

      <!-- Screened Date -->
      <div v-if="profile.screened_at && $date(profile.screened_at).isValid()" class="flex gap-2">
        <sword-icon name="calendar" />
        <p class="t3 m-0">
          <span class="select-none">{{ $t('patient_state_enrolled') }}: </span>
          <span :title="$date(profile.screened_at).format('llll')">{{ $date(profile.screened_at).format('MMM D') }}</span>
          <span>, {{ $date().defaultDiff(profile.screened_at) }}</span>
        </p>
      </div>

      <!-- Quick Actions -->
      <div class="relative mt-1">
        <quick-action
          v-for="(action, key) in quickActions"
          :key="`quick-action-${key}`"
          :action-key="action.key"
          :icon="action.icon"
          :tooltip="action.tooltip"
          :options="action.options"
          @action="handleClickAction"
        />
      </div>
    </section>
  </header>
</template>
